
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MockAPI } from '../services/api';
import { Document, RecordLink, DocumentSource, Farm, Asset, OMTask, MapFeature } from '../types';
import { compressImage } from '../services/imageUtils';
import { Card, Button, Badge } from '../components/ui';
import { FileText, Search, Trash2, Download, Image as ImageIcon, Map as MapIcon, Link as LinkIcon, Upload, FolderOpen, X, Save, Camera } from 'lucide-react';
import { useAuth } from '../services/authContext';
import { ServerRepository } from '../services/serverRepository.js';
import { getEntityMetadata } from '../services/dataAccessMap.js';
import { getAuthToken } from '../services/auth';
import PhotoCaptureFlow,{type CapturedPhotoFile} from '../components/PhotoCaptureFlow';
import {useFarm} from '../services/farmContext';

interface EnrichedDocument extends Document {
  linkedEntityNames: string[];
  linkedEntityTypes: string[];
}

const DocumentVault: React.FC = () => {
  const navigate = useNavigate();
  const { user,privileges } = useAuth();
  const {activeFarmId}=useFarm();
  const [photoCapture,setPhotoCapture]=useState<CapturedPhotoFile[]|null>(null);
  const [loadError,setLoadError]=useState('');
  const canManageDocuments = !!privileges.documents?.write;
  const canViewTasks=Object.entries(privileges).some(([key,value])=>key==='maintenance'&&value.view);
  const documentRepo = React.useMemo(
    () => new ServerRepository<Document>('documents', getEntityMetadata('documents').primaryKey),
    []
  );
  const linkRepo = React.useMemo(
    () => new ServerRepository<RecordLink>('links', getEntityMetadata('links').primaryKey),
    []
  );
  const [documents, setDocuments] = useState<EnrichedDocument[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  
  // Upload Modal State
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadEntityType, setUploadEntityType] = useState<string>('none');
  const [uploadEntityId, setUploadEntityId] = useState<string>('');
  const [uploadNotes, setUploadNotes] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Dropdown Data
  const [availableFarms, setAvailableFarms] = useState<Farm[]>([]);
  const [availableAssets, setAvailableAssets] = useState<Asset[]>([]);
  const [availableTasks, setAvailableTasks] = useState<OMTask[]>([]);
  const [availableMapFeatures, setAvailableMapFeatures] = useState<MapFeature[]>([]);

  const refreshData = async () => {
    const docs = await documentRepo.list();
    const links = await linkRepo.list();
    
    const farmsData = privileges.map?.view?await MockAPI.getFarms():[];
    const assetsData = privileges.assets?.view?await MockAPI.getAssets():[];
    const tasksData = canViewTasks?await MockAPI.getTasks():[];

    const farms = new Map(farmsData.map(f => [f.farm_id, f.name]));
    const assets = new Map(assetsData.map(a => [a.asset_id, a.name]));
    const tasks = new Map(tasksData.map(t => [t.task_id, `${t.task_type} (${t.scheduled_date})`]));
    const projectsData = privileges.projects?.view?await MockAPI.getProjects():[];
    const projects = new Map(projectsData.map((p) => [p.id, p.name]));

    const enriched: EnrichedDocument[] = docs.map(doc => {
      const docLinks = links.filter((l) => l.document_id === doc.document_id);

      const names = docLinks.map((link) => {
        const entityId = link.entity_id;
        switch(link.entity_type) {
          case 'farm': return farms.get(entityId as any) || `Farm #${entityId}`;
          case 'asset': return assets.get(entityId as any) || `Asset #${entityId}`;
          case 'task': return tasks.get(entityId as any) || `Task #${entityId}`;
          case 'map_feature': return `Place #${entityId}`;
          case 'project': return projects.get(entityId as any) || `Project #${entityId}`;
          default: return `${link.entity_type} #${entityId}`;
        }
      });

      return {
        ...doc,
        linkedEntityNames: names,
        linkedEntityTypes: docLinks.map((link) => link.entity_type || link.from_record_type),
      };
    });

    enriched.sort((a, b) => new Date(b.uploaded_date).getTime() - new Date(a.uploaded_date).getTime());
    setDocuments(enriched);
  };

  useEffect(() => {
    refreshData();
  }, []);

  useEffect(() => {
      const loadOptions = async () => {
        if (isUploadModalOpen) {
            setAvailableFarms(privileges.map?.view?await MockAPI.getFarms():[]);
            setAvailableAssets(privileges.assets?.view?await MockAPI.getAssets():[]);
            setAvailableTasks(canViewTasks?await MockAPI.getTasks():[]);
            
            const farms = privileges.map?.view?await MockAPI.getFarms():[];
            const mapFeatures = (await Promise.all(farms.map((farm) => MockAPI.getMapFeatures(farm.farm_id)))).flat();
            setAvailableMapFeatures(mapFeatures);
        }
      };
      void loadOptions().catch(()=>setLoadError('Some document link options could not load.'));
  }, [isUploadModalOpen]);

  const handleDelete = async (id:string) => {
    if(!canManageDocuments||!confirm('Delete this document? Its attached photos will no longer be available here.'))return;
    try{await documentRepo.remove(id);await refreshData();setLoadError('');}
    catch{setLoadError('The document could not be deleted. Check your access and retry.');}
  };

  const handleUploadSubmit = async () => {
      if (!uploadFile) return;
      if(uploadFile.type.startsWith('image/')||/\.(jpe?g|png|webp|heic|heif)$/i.test(uploadFile.name)){setPhotoCapture([{file:uploadFile,account:user!.email}]);setIsUploadModalOpen(false);setUploadFile(null);return;}
      setIsProcessing(true);

      try {
          // Check file size (limit to ~2MB for localStorage safety)
          if (uploadFile.size > 2 * 1024 * 1024 && !uploadFile.type.startsWith('image/')) {
              alert("File too large. Please upload files smaller than 2MB for the local prototype.");
              setIsProcessing(false);
              return;
          }

          const base64 = await compressImage(uploadFile);

          const newDoc: Document = {
              document_id: '',
              title: uploadFile.name,
              file_path: `/uploads/${uploadFile.name}`, // Keeping for legacy/display compatibility
              file_base64: base64,
              file_type: uploadFile.name.split('.').pop() || 'file',
              source: DocumentSource.Uploaded,
              uploaded_date: new Date().toISOString(),
              is_confidential: false,
              notes: uploadNotes || 'Uploaded via Vault',
              server_version: 0
          };
          
          const savedDoc = await MockAPI.saveDocument(newDoc);

          if (uploadEntityType !== 'none' && uploadEntityId) {
               const newLink: RecordLink = {
                   record_link_id: '0',
                   from_record_type: 'document',
                   from_record_id: savedDoc.document_id,
                   to_record_type: uploadEntityType as any,
                   to_record_id: uploadEntityId,
                   entity_type: uploadEntityType as any,
                   entity_id: uploadEntityId,
                   document_id: savedDoc.document_id,
                   linked_date: new Date().toISOString(),
                   linked_by: 'User',
                   server_version: 0
               };
               await MockAPI.saveRecordLink(newLink);
          }

          setIsUploadModalOpen(false);
          setUploadFile(null);
          setUploadEntityType('none');
          setUploadEntityId('');
          setUploadNotes('');
          refreshData();
      } catch (err) {
          console.error("Upload failed", err);
          const message = err instanceof Error ? err.message : 'Failed to save file.';
          alert(`Failed to save file: ${message}`);
      } finally {
          setIsProcessing(false);
      }
  };

  const handleDownload = async (doc: Document): Promise<void> => {
      const directSource = doc.file_base64;
      if (directSource) {
          const link = document.createElement('a');
          link.href = directSource;
          link.download = doc.title;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
      }

      const downloadUrl = doc.presigned_url || doc.file_path;
      if (!downloadUrl) {
          alert("This is a simulated document (seed data) and cannot be downloaded.");
          return;
      }

      try {
          const headers: HeadersInit = {};
          const token = getAuthToken();
          if (token) headers['Authorization'] = `Bearer ${token}`;

          const response = await fetch(downloadUrl, { headers });
          if (!response.ok) {
              throw new Error(`Download failed (${response.status})`);
          }

          const blob = await response.blob();
          const objectUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = doc.title;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(objectUrl);
      } catch (err) {
          console.error('Download failed', err);
          alert(`Download failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
  };

  const filteredDocs = useMemo(() => {
      return documents.filter(d => {
          const matchesSearch = d.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                d.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                d.linkedEntityNames.some((name) => name.toLowerCase().includes(searchTerm.toLowerCase()));
          const matchesSource = sourceFilter === 'all' || d.source === sourceFilter;
          return matchesSearch && matchesSource;
      });
  }, [documents, searchTerm, sourceFilter]);

  const getIcon = (type?: string) => {
      const t = type?.toLowerCase();
      if (['jpg','jpeg','png','gif'].includes(t || '')) return <ImageIcon className="h-5 w-5 text-purple-600" />;
      if (['gpx','kml','geojson'].includes(t || '')) return <MapIcon className="h-5 w-5 text-green-600" />;
      return <FileText className="h-5 w-5 text-blue-600" />;
  }

  const getDisplayFileName = (doc: Document) => {
      const filePathName = doc.file_path?.split('/').pop();
      return filePathName || doc.title;
  };

  const getDisplayDocumentName = (doc: Document) => {
      return doc.notes?.trim() || doc.title;
  };

  return (
    <div className="space-y-6 relative">
      {photoCapture&&canManageDocuments&&<PhotoCaptureFlow createDocument initialFarmId={activeFarmId||undefined} initialFiles={photoCapture} onCancel={()=>setPhotoCapture(null)} onComplete={()=>setPhotoCapture(null)}/>}
      {loadError&&<p role="alert" className="text-red-700">{loadError}</p>}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <FolderOpen className="h-6 w-6 mr-2 text-usda-600" /> 
            Document Vault
          </h1>
          <p className="mt-1 text-sm text-gray-500">Centralized file management for all farm records.</p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-wrap gap-2">
           <Button variant="outline" disabled={!canManageDocuments} onClick={()=>setPhotoCapture([])}><Camera className="h-4 w-4 mr-2"/>Create from photo</Button>
           <Button onClick={() => setIsUploadModalOpen(true)} disabled={!canManageDocuments} title={canManageDocuments ? undefined : 'You do not have permission to upload documents'}>
               <Upload className="h-4 w-4 mr-2" />
               Upload Document
           </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-4 flex items-center space-x-4">
              <div className="p-3 bg-blue-100 rounded-full">
                  <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                  <div className="text-2xl font-bold text-gray-900">{documents.length}</div>
                  <div className="text-xs text-gray-500 uppercase font-medium">Total Files</div>
              </div>
          </Card>
           <Card className="p-4 flex items-center space-x-4">
              <div className="p-3 bg-green-100 rounded-full">
                  <LinkIcon className="h-6 w-6 text-green-600" />
              </div>
              <div>
                  <div className="text-2xl font-bold text-gray-900">{documents.filter((d) => d.linkedEntityNames.length > 0).length}</div>
                  <div className="text-xs text-gray-500 uppercase font-medium">Linked Records</div>
              </div>
          </Card>
          <Card className="p-4 flex items-center space-x-4">
              <div className="p-3 bg-gray-100 rounded-full">
                  <Upload className="h-6 w-6 text-gray-600" />
              </div>
              <div>
                  <div className="text-2xl font-bold text-gray-900">{documents.filter(d => d.source === DocumentSource.Uploaded).length}</div>
                  <div className="text-xs text-gray-500 uppercase font-medium">User Uploads</div>
              </div>
          </Card>
      </div>

      <Card className="overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative max-w-sm w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                    type="text"
                    placeholder="Search files..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                />
            </div>
            <div className="flex items-center space-x-2">
                <select
                    value={sourceFilter}
                    onChange={(e) => setSourceFilter(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                >
                    <option value="all">All Sources</option>
                    {Object.values(DocumentSource).map(s => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
            </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linked To</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type/Source</th>
                        <th scope="col" className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {filteredDocs.map((doc) => (
                        <tr
                            key={doc.document_id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => navigate(`/documents/${doc.document_id}`)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    navigate(`/documents/${doc.document_id}`);
                                }
                            }}
                            tabIndex={0}
                        >
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0 mr-3">
                                        {getIcon(doc.file_type)}
                                    </div>
                                    <div className="overflow-hidden">
                                        <div className="text-sm font-medium text-usda-700 truncate max-w-xs" title={getDisplayDocumentName(doc)}>{getDisplayDocumentName(doc)}</div>
                                        <div className="text-xs text-gray-500 truncate max-w-xs" title={getDisplayFileName(doc)}>{getDisplayFileName(doc)}</div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                {doc.linkedEntityNames.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {doc.linkedEntityNames.map((name) => (
                                        <Badge key={`${doc.document_id}-${name}`} variant="neutral">
                                          {name}
                                        </Badge>
                                      ))}
                                    </div>
                                ) : (
                                    <span className="text-xs text-gray-400 italic">Unlinked</span>
                                )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(doc.uploaded_date).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div className="flex flex-col">
                                    <span className="uppercase text-xs font-bold">{doc.file_type}</span>
                                    <span className="text-xs">{doc.source}</span>
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                                <button 
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleDownload(doc);
                                    }}
                                    className="text-usda-600 hover:text-usda-900" 
                                    title="Download"
                                >
                                    <Download className="h-4 w-4" />
                                </button>
                                <button 
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleDelete(doc.document_id);
                                    }}
                                    className="text-red-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed" 
                                    title="Delete"
                                    disabled={!canManageDocuments}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </td>
                        </tr>
                    ))}
                    {filteredDocs.length === 0 && (
                        <tr>
                            <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                                No documents found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
      </Card>

      {/* Upload Modal */}
      {isUploadModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
                  <div className="flex items-center justify-between p-4 border-b bg-gray-50">
                      <h2 className="text-lg font-bold text-gray-900">Upload Document</h2>
                      <button onClick={() => setIsUploadModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                          <X className="h-5 w-5" />
                      </button>
                  </div>
                  
                  <div className="p-6 space-y-4">
                      {/* File Input */}
                      <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Select File or Take Photo</label>
                          <input 
                              type="file" 
                              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,application/pdf"
                              onChange={(e) => {const file=e.target.files?.[0];if(file&&(file.type.startsWith('image/')||/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name))){setPhotoCapture([{file,account:user!.email}]);setIsUploadModalOpen(false);setUploadFile(null);}else setUploadFile(file||null);}}
                              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-usda-50 file:text-usda-700 hover:file:bg-usda-100"
                          />
                          <p className="mt-1 text-xs text-gray-500 flex items-center">
                            <Camera className="h-3 w-3 mr-1" /> Camera supported for photos.
                          </p>
                      </div>

                      {/* Notes */}
                      <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Notes / Description</label>
                          <input 
                              type="text" 
                              value={uploadNotes}
                              onChange={(e) => setUploadNotes(e.target.value)}
                              placeholder="Optional description..."
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                          />
                      </div>

                      <div className="border-t border-gray-100 pt-4">
                          <label className="block text-sm font-medium text-gray-900 mb-2">Link to Record (Optional)</label>
                          
                          <div className="grid grid-cols-1 gap-4">
                              <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">Record Type</label>
                                  <select 
                                      value={uploadEntityType} 
                                      onChange={(e) => { setUploadEntityType(e.target.value); setUploadEntityId(''); }}
                                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                                  >
                                      <option value="none">-- None --</option>
                                      <option value="farm">Farm</option>
                                      <option value="asset">Asset (Equipment)</option>
                                      <option value="task">Maintenance Task</option>
                                      <option value="map_feature">Place</option>
                                  </select>
                              </div>

                              {uploadEntityType !== 'none' && (
                                  <div>
                                      <label className="block text-xs font-medium text-gray-500 mb-1">Select Record</label>
                                      <select 
                                          value={uploadEntityId} 
                                          onChange={(e) => setUploadEntityId(e.target.value)}
                                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                                      >
                                          <option value="">-- Select --</option>
                                          {uploadEntityType === 'farm' && availableFarms.map(f => (
                                              <option key={f.farm_id} value={f.farm_id}>{f.name}</option>
                                          ))}
                                          {uploadEntityType === 'asset' && availableAssets.map(a => (
                                              <option key={a.asset_id} value={a.asset_id}>{a.name}</option>
                                          ))}
                                          {uploadEntityType === 'task' && availableTasks.map(t => (
                                              <option key={t.task_id} value={t.task_id}>{t.task_type} - {t.scheduled_date || 'No Date'}</option>
                                          ))}
                                          {uploadEntityType === 'map_feature' && availableMapFeatures.map((feature) => (
                                              <option key={feature.map_feature_id} value={feature.map_feature_id}>{feature.label || feature.map_feature_id}</option>
                                          ))}
                                      </select>
                                  </div>
                              )}
                          </div>
                      </div>
                  </div>

                  <div className="p-4 border-t bg-gray-50 flex justify-end space-x-3">
                      <Button variant="outline" onClick={() => setIsUploadModalOpen(false)}>Cancel</Button>
                      <Button onClick={handleUploadSubmit} disabled={!uploadFile || isProcessing}>
                          <Save className="h-4 w-4 mr-2" /> {isProcessing ? 'Saving...' : 'Upload'}
                      </Button>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

export default function ScopedDocumentVault(){const {user,privileges}=useAuth();return <DocumentVault key={JSON.stringify([user?.email,user?.status,user?.farm_ids,user?.roles,privileges])}/>;}
